import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useAppLoading } from '@/stores/app/loading';
import { getArchitectureTree, getProfilesByUlids } from '@/api/architecture';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { normalizeCustomFields } from './helpers';

export const useJobArchitecture = defineStore('jobArchitecture', () => {
  const { setLoading } = useAppLoading();
  const snackbar = useAppSnackbar();

  const treeData = ref(null);
  const selectedFamily = ref(null);
  const selectedFamilyProfiles = ref([]);

  // Set of expanded group node IDs
  const expandedGroupIds = ref(new Set());

  const loadTree = async () => {
    setLoading({ pageLoading: true });

    try {
      const response = await getArchitectureTree();

      treeData.value = response;

      return treeData.value;
    } catch (err) {
      snackbar.addErrorSnackbar({
        text: `Job Architecture load error: ${err.message || err}`,
      });

      throw err;
    } finally {
      setLoading({ pageLoading: false });
    }
  };

  const selectFamily = async (family) => {
    selectedFamily.value = family;
    selectedFamilyProfiles.value = [];

    if (!family) {
      return;
    }

    const ulids = (family.profiles || []).map((profile) => profile.ulid).filter(Boolean);

    setLoading({ listLoading: true });

    try {
      const profiles = ulids.length > 0 ? await getProfilesByUlids(ulids) : [];

      const normalized = (profiles || []).map((profile) => {
        const customFields = normalizeCustomFields(profile.custom_fields);

        return { ...profile, custom_fields: customFields };
      });

      selectedFamilyProfiles.value = normalized;
    } catch (err) {
      snackbar.addErrorSnackbar({
        text: `Job profiles load error: ${err.message || err}`,
      });

      selectedFamilyProfiles.value = [];
    } finally {
      setLoading({ listLoading: false });
    }
  };

  const clearSelection = () => {
    selectedFamily.value = null;
    selectedFamilyProfiles.value = [];
  };

  const toggleGroupExpanded = (groupNodeId) => {
    const updatedExpandedGroupIds = new Set(expandedGroupIds.value);

    if (updatedExpandedGroupIds.has(groupNodeId)) updatedExpandedGroupIds.delete(groupNodeId);
    else updatedExpandedGroupIds.add(groupNodeId);

    expandedGroupIds.value = updatedExpandedGroupIds;
  };

  return {
    treeData,
    selectedFamily,
    selectedFamilyProfiles,
    expandedGroupIds,
    loadTree,
    selectFamily,
    clearSelection,
    toggleGroupExpanded,
  };
});
